import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  UserPlus, Users, Shield, UserX, MoreHorizontal, Pencil,
  Search, MapPin, Phone, Mail, Building2, Globe, CheckCircle2,
  Clock, XCircle, RefreshCw, Loader2, Filter, ChevronDown,
} from "lucide-react";
import { useGaContext } from "@/hooks/useGaContext";

// ── Constants ─────────────────────────────────────────────────────────────────

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

const DEPARTMENTS = [
  "Underwriting", "Sales", "Account Management", "Operations",
  "Compliance", "Marketing", "Technology", "Finance", "Executive", "Other",
];

// ── Role definitions ──────────────────────────────────────────────────────────

export const GA_ROLES: Record<string, { label: string; color: string; icon: any; description: string }> = {
  ga_admin:           { label: "GA Admin",           color: "bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800",  icon: Shield,      description: "Full portal access — can manage users, licenses, settings, and API connections." },
  ga_service_rep:     { label: "Service Rep",         color: "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800",        icon: Users,       description: "Access to broker clients and case submissions. Cannot manage licenses or users." },
  ga_inside_sales:    { label: "Inside Sales Rep",    color: "bg-cyan-100 dark:bg-cyan-900 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800",        icon: Users,       description: "Handles inbound broker inquiries, quoting support, and pipeline follow-up." },
  ga_outside_sales:   { label: "Outside Sales Rep",   color: "bg-teal-100 dark:bg-teal-900 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800",        icon: Users,       description: "Field-based broker relationship management and new account development." },
  ga_underwriter:     { label: "Underwriter",         color: "bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",     icon: Shield,      description: "Reviews and approves group cases, assesses risk, and sets terms." },
  ga_account_manager: { label: "Account Manager",     color: "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800",     icon: Users,       description: "Manages ongoing broker relationships, renewals, and service issues." },
  ga_operations:      { label: "Operations",          color: "bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800",  icon: Users,       description: "Handles enrollment processing, eligibility, and back-office workflows." },
  ga_compliance:      { label: "Compliance",          color: "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800",           icon: Shield,      description: "Monitors regulatory requirements, licensing, and audit readiness." },
  ga_marketing:       { label: "Marketing",           color: "bg-pink-100 dark:bg-pink-900 text-pink-700 dark:text-pink-300 border-pink-200 dark:border-pink-800",        icon: Users,       description: "Manages broker-facing communications, campaigns, and materials." },
};

// ── Badge components ──────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: string }) {
  const cfg = GA_ROLES[role];
  if (!cfg) return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">{role}</span>;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
      <Icon className="w-2.5 h-2.5" /> {cfg.label}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, { label: string; className: string; icon: any }> = {
    active:      { label: "Active",      className: "bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800",  icon: CheckCircle2 },
    invited:     { label: "Invited",     className: "bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",  icon: Clock },
    deactivated: { label: "Deactivated", className: "bg-muted text-muted-foreground border-border",    icon: XCircle },
  };
  const c = cfg[status] || cfg.deactivated;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${c.className}`}>
      <Icon className="w-2.5 h-2.5" /> {c.label}
    </span>
  );
}

// ── Avatar initials helper ────────────────────────────────────────────────────

function UserAvatar({ user, size = "md" }: { user: any; size?: "sm" | "md" | "lg" }) {
  const sizes = { sm: "w-7 h-7 text-xs", md: "w-9 h-9 text-sm", lg: "w-12 h-12 text-base" };
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || "?";
  const initials = name === "?" ? "?" : name.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2);
  const colors = ["bg-blue-500","bg-purple-500","bg-green-500","bg-amber-500","bg-pink-500","bg-indigo-500","bg-teal-500","bg-red-500"];
  const color = colors[(initials.charCodeAt(0) || 0) % colors.length];
  return (
    <div className={`${sizes[size]} ${color} rounded-full flex items-center justify-center text-white font-semibold shrink-0`}>
      {initials}
    </div>
  );
}

// ── State Picker ──────────────────────────────────────────────────────────────

function StatesPicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const toggle = (s: string) => onChange(value.includes(s) ? value.filter((x) => x !== s) : [...value, s]);
  return (
    <div>
      <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-2 rounded-md border border-border bg-muted">
        {US_STATES.map((s) => (
          <button key={s} type="button" onClick={() => toggle(s)}
            className={`text-xs font-medium px-1.5 py-0.5 rounded transition-colors ${value.includes(s) ? "bg-blue-600 text-white" : "bg-card text-muted-foreground border border-border hover:border-blue-300 dark:hover:border-blue-700"}`}>
            {s}
          </button>
        ))}
      </div>
      {value.length > 0 && (
        <p className="text-xs text-muted-foreground mt-1">
          {value.length} state{value.length !== 1 ? "s" : ""}: <span className="font-medium">{value.join(", ")}</span>
        </p>
      )}
    </div>
  );
}

// ── User Form (shared between Invite + Edit dialogs) ─────────────────────────

const blankForm = {
  email: "", firstName: "", lastName: "", phone: "", title: "",
  department: "", role: "ga_service_rep", locationId: "",
  assignedStates: [] as string[], notes: "",
};

function UserFormFields({ form, setForm, locations, isInvite = false }: {
  form: typeof blankForm;
  setForm: (f: typeof blankForm) => void;
  locations: any[];
  isInvite?: boolean;
}) {
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  return (
    <div className="space-y-5">
      {/* Identity */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Identity</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>First Name</Label>
            <Input value={form.firstName} onChange={(e) => set("firstName", e.target.value)} placeholder="Alex" />
          </div>
          <div className="space-y-1.5">
            <Label>Last Name</Label>
            <Input value={form.lastName} onChange={(e) => set("lastName", e.target.value)} placeholder="Johnson" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Email Address {isInvite && <span className="text-red-500 dark:text-red-400">*</span>}</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="alex@agency.com" />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 000-0000" />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(GA_ROLES).map(([value, { label }]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Position */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Position</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Job Title</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Underwriter" />
          </div>
          <div className="space-y-1.5">
            <Label>Department</Label>
            <Select value={form.department || "none"} onValueChange={(v) => set("department", v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Office / Location */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Office Location</p>
        <Select value={form.locationId} onValueChange={(v) => set("locationId", v === "none" ? "" : v)}>
          <SelectTrigger><SelectValue placeholder="Assign to a location…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No specific location</SelectItem>
            {locations.map((loc: any) => (
              <SelectItem key={loc.id} value={loc.id}>
                <span className="flex items-center gap-2">
                  {loc.isPrimary && <span className="text-blue-500 dark:text-blue-400">★</span>}
                  {loc.name}{loc.city ? ` — ${loc.city}${loc.state ? `, ${loc.state}` : ""}` : ""}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Assigned States */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Assigned States</p>
        <p className="text-xs text-muted-foreground mb-2">States this user is responsible for or licensed in.</p>
        <StatesPicker value={form.assignedStates} onChange={(v) => set("assignedStates", v)} />
      </div>

      {/* Notes */}
      <div className="space-y-1.5">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
          placeholder="Any internal notes about this user…" rows={2} className="resize-none" />
      </div>
    </div>
  );
}

// ── Invite Dialog ─────────────────────────────────────────────────────────────

function InviteDialog({ open, onClose, gaId, locations }: {
  open: boolean; onClose: () => void; gaId: string; locations: any[];
}) {
  const { toast } = useToast();
  const [form, setForm] = useState(blankForm);

  useEffect(() => { if (open) setForm(blankForm); }, [open]);

  const mutation = useMutation({
    mutationFn: (data: typeof form) => apiRequest("POST", `/api/ga/${gaId}/users/invite`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "users"] });
      toast({ title: "Invite sent", description: "User has been added with invited status." });
      onClose();
    },
    onError: () => toast({ title: "Error", description: "Failed to send invite.", variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <UserFormFields form={form} setForm={setForm} locations={locations} isInvite />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate(form)} disabled={!form.email || mutation.isPending}>
            {mutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : <><UserPlus className="w-4 h-4 mr-2" />Send Invite</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Dialog ───────────────────────────────────────────────────────────────

function EditDialog({ open, onClose, gaId, user, locations }: {
  open: boolean; onClose: () => void; gaId: string; user: any; locations: any[];
}) {
  const { toast } = useToast();
  const [form, setForm] = useState(blankForm);

  useEffect(() => {
    if (user) {
      setForm({
        email: user.email || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: user.phone || "",
        title: user.title || "",
        department: user.department || "",
        role: user.role || "ga_service_rep",
        locationId: user.locationId || "",
        assignedStates: user.assignedStates || [],
        notes: user.notes || "",
      });
    }
  }, [user]);

  const mutation = useMutation({
    mutationFn: (data: typeof form) => apiRequest("PATCH", `/api/ga/${gaId}/users/${user.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "users"] });
      toast({ title: "User updated" });
      onClose();
    },
    onError: () => toast({ title: "Error", description: "Failed to update user.", variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <UserFormFields form={form} setForm={setForm} locations={locations} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate(form)} disabled={mutation.isPending}>
            {mutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function GAUsers() {
  const { gaId } = useGaContext();
  const { toast } = useToast();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<any>(null);
  const [reactivateTarget, setReactivateTarget] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLocation, setFilterLocation] = useState("all");

  const { data: users = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "users"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/users`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const { data: locations = [] } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "locations"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/locations`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/ga/${gaId}/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "users"] });
      setDeactivateTarget(null);
      toast({ title: "User deactivated" });
    },
    onError: () => toast({ title: "Error", description: "Failed to deactivate user.", variant: "destructive" }),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/ga/${gaId}/users/${id}`, { status: "active" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "users"] });
      setReactivateTarget(null);
      toast({ title: "User reactivated" });
    },
    onError: () => toast({ title: "Error", description: "Failed to reactivate user.", variant: "destructive" }),
  });

  // Location lookup map
  const locationMap: Record<string, any> = {};
  locations.forEach((l: any) => { locationMap[l.id] = l; });

  // Display name
  const displayName = (u: any) =>
    [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email || "Unknown";

  // Filtered + searched users
  const filtered = users.filter((u: any) => {
    const q = search.toLowerCase();
    const matchSearch = !q || displayName(u).toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) ||
      (u.title || "").toLowerCase().includes(q) || (u.department || "").toLowerCase().includes(q);
    const matchRole = filterRole === "all" || u.role === filterRole;
    const matchStatus = filterStatus === "all" || u.status === filterStatus;
    const matchLoc = filterLocation === "all" || (filterLocation === "none" ? !u.locationId : u.locationId === filterLocation);
    return matchSearch && matchRole && matchStatus && matchLoc;
  });

  // Summary counts
  const counts = { active: 0, invited: 0, deactivated: 0, admin: 0, rep: 0 };
  users.forEach((u: any) => {
    if (u.status === "active") counts.active++;
    else if (u.status === "invited") counts.invited++;
    else counts.deactivated++;
    if (u.role === "ga_admin") counts.admin++;
    else counts.rep++;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Users</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage team members, roles, and territory assignments</p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <UserPlus className="w-4 h-4 mr-2" /> Invite User
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Active", value: counts.active, color: "text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800" },
          { label: "Invited", value: counts.invited, color: "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800" },
          { label: "Deactivated", value: counts.deactivated, color: "text-muted-foreground bg-muted border-border" },
          { label: "Admins", value: counts.admin, color: "text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950 border-purple-200 dark:border-purple-800" },
          { label: "Service Reps", value: counts.rep, color: "text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800" },
        ].map((c) => (
          <div key={c.label} className={`rounded-xl border px-4 py-3 ${c.color}`}>
            <p className="text-2xl font-bold">{c.value}</p>
            <p className="text-xs font-medium mt-0.5 opacity-80">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input className="pl-8 h-8 text-sm" placeholder="Search by name, email, title…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-8 text-xs w-32">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="invited">Invited</SelectItem>
            <SelectItem value="deactivated">Deactivated</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="h-8 text-xs w-40">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {Object.entries(GA_ROLES).map(([value, { label }]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {locations.length > 0 && (
          <Select value={filterLocation} onValueChange={setFilterLocation}>
            <SelectTrigger className="h-8 text-xs w-40">
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              <SelectItem value="none">No Location</SelectItem>
              {locations.map((l: any) => (
                <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {(search || filterRole !== "all" || filterStatus !== "all" || filterLocation !== "all") && (
          <Button variant="ghost" size="sm" className="h-8 text-xs"
            onClick={() => { setSearch(""); setFilterRole("all"); setFilterStatus("all"); setFilterLocation("all"); }}>
            Clear filters
          </Button>
        )}
      </div>

      {/* User list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed border-border rounded-xl bg-muted">
          <Users className="w-10 h-10 text-muted-foreground mb-3" />
          <p className="text-sm font-medium text-foreground">
            {users.length === 0 ? "No team members yet" : "No users match your filters"}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {users.length === 0 ? "Invite your first team member to get started" : "Try adjusting your search or filters"}
          </p>
          {users.length === 0 && (
            <Button className="mt-4" size="sm" onClick={() => setInviteOpen(true)}>
              <UserPlus className="w-4 h-4 mr-1.5" /> Invite First User
            </Button>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Member</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Role</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Office</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">States</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((u: any) => {
                const loc = u.locationId ? locationMap[u.locationId] : null;
                const states: string[] = u.assignedStates || [];
                const name = displayName(u);
                return (
                  <tr key={u.id} className={`hover:bg-muted/50 transition-colors ${u.status === "deactivated" ? "opacity-50" : ""}`}>
                    {/* Member */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <UserAvatar user={u} />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate">{name}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {u.email && (
                              <p className="text-xs text-muted-foreground flex items-center gap-0.5">
                                <Mail className="w-2.5 h-2.5" /> {u.email}
                              </p>
                            )}
                            {u.phone && (
                              <p className="text-xs text-muted-foreground flex items-center gap-0.5">
                                <Phone className="w-2.5 h-2.5" /> {u.phone}
                              </p>
                            )}
                          </div>
                          {(u.title || u.department) && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {[u.title, u.department].filter(Boolean).join(" · ")}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Role */}
                    <td className="px-4 py-3"><RoleBadge role={u.role} /></td>

                    {/* Office */}
                    <td className="px-4 py-3">
                      {loc ? (
                        <div className="flex items-start gap-1">
                          <MapPin className="w-3 h-3 text-muted-foreground mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-foreground">{loc.name}</p>
                            {loc.city && <p className="text-xs text-muted-foreground">{loc.city}{loc.state ? `, ${loc.state}` : ""}</p>}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>

                    {/* States */}
                    <td className="px-4 py-3">
                      {states.length === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : states.length <= 4 ? (
                        <div className="flex flex-wrap gap-0.5">
                          {states.map((s) => (
                            <span key={s} className="inline-block bg-muted text-muted-foreground text-[10px] font-semibold px-1.5 py-0.5 rounded">
                              {s}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-0.5 items-center">
                          {states.slice(0, 3).map((s) => (
                            <span key={s} className="inline-block bg-muted text-muted-foreground text-[10px] font-semibold px-1.5 py-0.5 rounded">
                              {s}
                            </span>
                          ))}
                          <span className="text-[10px] text-muted-foreground font-medium">+{states.length - 3} more</span>
                        </div>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        <StatusBadge status={u.status} />
                        <p className="text-[10px] text-muted-foreground">
                          {u.status === "invited" && u.invitedAt ? `Invited ${new Date(u.invitedAt).toLocaleDateString()}` :
                           u.status === "active" && u.acceptedAt ? `Since ${new Date(u.acceptedAt).toLocaleDateString()}` : ""}
                        </p>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => setEditTarget(u)}>
                            <Pencil className="w-3.5 h-3.5 mr-2" /> Edit Profile
                          </DropdownMenuItem>
                          {u.status === "invited" && (
                            <DropdownMenuItem onClick={() => toast({ title: "Invite resent" })}>
                              <RefreshCw className="w-3.5 h-3.5 mr-2" /> Resend Invite
                            </DropdownMenuItem>
                          )}
                          {u.status === "deactivated" ? (
                            <DropdownMenuItem className="text-green-600 dark:text-green-400" onClick={() => setReactivateTarget(u)}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> Reactivate
                            </DropdownMenuItem>
                          ) : (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-red-600 dark:text-red-400" onClick={() => setDeactivateTarget(u)}>
                                <UserX className="w-3.5 h-3.5 mr-2" /> Deactivate
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > 0 && (
            <div className="px-4 py-2.5 bg-muted border-t border-border">
              <p className="text-xs text-muted-foreground">
                Showing {filtered.length} of {users.length} user{users.length !== 1 ? "s" : ""}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Role legend */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Role Descriptions</p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
          {Object.entries(GA_ROLES).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const classes = cfg.color.split(" ");
            const textColor = classes.filter((c) => c.startsWith("text-") || c.startsWith("dark:text-")).join(" ") || "text-foreground";
            const borderColor = classes.filter((c) => c.startsWith("border-") || c.startsWith("dark:border-")).join(" ") || "border-border";
            const bgColor = classes.filter((c) => c.startsWith("bg-") || c.startsWith("dark:bg-")).map((c) => `${c}/40`).join(" ") || "bg-muted/40";
            return (
              <div key={key} className={`rounded-xl border ${borderColor} ${bgColor} p-3`}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-3.5 h-3.5 ${textColor}`} />
                  <p className={`text-xs font-semibold ${textColor}`}>{cfg.label}</p>
                </div>
                <p className={`text-[11px] leading-relaxed ${textColor} opacity-80`}>{cfg.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dialogs */}
      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} gaId={gaId || ""} locations={locations} />

      {editTarget && (
        <EditDialog open={!!editTarget} onClose={() => setEditTarget(null)}
          gaId={gaId || ""} user={editTarget} locations={locations} />
      )}

      <AlertDialog open={!!deactivateTarget} onOpenChange={(o) => !o && setDeactivateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate User</AlertDialogTitle>
            <AlertDialogDescription>
              Deactivate <strong>{deactivateTarget ? displayName(deactivateTarget) : ""}</strong>? They will immediately lose access to the GA portal.
              The record is preserved and can be reactivated later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700"
              onClick={() => deactivateTarget && deactivateMutation.mutate(deactivateTarget.id)}>
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!reactivateTarget} onOpenChange={(o) => !o && setReactivateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate User</AlertDialogTitle>
            <AlertDialogDescription>
              Restore portal access for <strong>{reactivateTarget ? displayName(reactivateTarget) : ""}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-green-600 hover:bg-green-700"
              onClick={() => reactivateTarget && reactivateMutation.mutate(reactivateTarget.id)}>
              Reactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
